import { rotateVectorByQuaternion } from "./qr-pose.js";
import { normalizeBearingDeg } from "../../utils/bearing-degrees.js";
import { worldNueToGps } from "../../visualization/frame-conversions.js";
//#region ../src/ar/qr/qr-geo-pose-minting.ts
/**
* A code is "near-vertical" (wall-poster convention, compat `headingDeg`
* emitted) when its local +y stays within this many degrees of world Up.
* Derivation (milestone review #7): the heading path idealizes tilt away,
* and a rotation-unaware reader pays ≈ baseline·sin(tilt) per wide-baseline
* correspondence — 3° at a 10 m ring is ≈0.52 m, inside the field-test's
* 2 m walk-away budget; the plan's earlier guardrail number was also 3°.
* Rotation-aware readers are unaffected (they use the exact quaternion).
*/
const VERTICAL_TOLERANCE_DEG = 3;
function mintQrGeoPose(input) {
	const { worldNuePosition, worldNueRotation, zero } = input;
	if (![
		worldNuePosition.x,
		worldNuePosition.y,
		worldNuePosition.z
	].every(Number.isFinite)) throw new RangeError("mintQrGeoPose: position must be finite");
	const rotation = normalizeUnitQuaternion(worldNueRotation);
	const gps = worldNueToGps(worldNuePosition, zero);
	const headingDeg = deriveVerticalHeading(rotation);
	return {
		lat: gps.lat,
		lon: gps.lon,
		alt: worldNuePosition.y,
		rotation,
		...headingDeg !== void 0 ? { headingDeg } : {}
	};
}
function normalizeUnitQuaternion(q) {
	const [x, y, z, w] = q;
	if (![
		x,
		y,
		z,
		w
	].every(Number.isFinite)) throw new RangeError("mintQrGeoPose: rotation must be finite");
	const renormalized = renormalizeUnitQuaternion(q);
	if (renormalized === void 0) throw new RangeError(`mintQrGeoPose: rotation must be a unit quaternion (|q| = ${Math.hypot(x, y, z, w)})`);
	return renormalized;
}
/**
* The ONE renormalization contract for authored/minted unit quaternions —
* shared by `mintQrGeoPose` (writer) and `parseQrLevel`'s rotation parsing
* (reader), so the two halves of the level-file round-trip can never
* disagree (DEC-H3: shared behaviour is unified). Accepts a norm within
* 1e-3 of 1 (JSON round-trip loss), returns `undefined` for anything
* further off (callers throw their own error type) — and for anything
* NON-FINITE or not four components long.
*
* That last part was missing until 2026-08-30 (PR #383 review) and the JSDoc
* claimed it anyway. `Math.hypot(NaN, 0, 0, 1)` is `NaN`, and
* `Math.abs(NaN - 1) > 1e-3` is FALSE, so the guard fell through, `scale`
* stayed 1 (`NaN > 1e-12` is false too) and a NaN quaternion came back as
* itself. A short array did the same: `Math.hypot(0, 0, 0, undefined)` is
* `NaN`, so `[0, 0, 0]` returned `[0, 0, 0, NaN]`. Every caller had grown its
* own `length === 4 && every(Number.isFinite)` prelude to compensate — three
* of them in `capture-geo-join.ts` alone, each added by a separate review
* round — which is the signal the check belonged in the callee.
*
* Renormalization is IDEMPOTENT: a norm already within 1e-12 of 1 passes
* the components through bit-exact. Dividing by a 1-within-rounding norm
* shifts each component a last-bit step per application, so a
* parse → serialize(re-validates) → parse cycle drifted 1 ULP and broke
* the exact round-trip property (CI seed on r574). One real division lands
* the norm within a few ULP of 1 — inside the threshold — making a second
* pass the identity. Also canonicalizes -0 → +0 (JSON cannot carry -0).
*/
function renormalizeUnitQuaternion(q) {
	if (q.length !== 4) return void 0;
	const [x, y, z, w] = q;
	const norm = Math.hypot(x, y, z, w);
	if (!Number.isFinite(norm)) return void 0;
	if (Math.abs(norm - 1) > .001) return void 0;
	const scale = Math.abs(norm - 1) > 1e-12 ? norm : 1;
	return [
		x / scale + 0,
		y / scale + 0,
		z / scale + 0,
		w / scale + 0
	];
}
/**
* The compat `headingDeg` — defined only when the code is near-vertical
* (local +y within {@link VERTICAL_TOLERANCE_DEG} of Up): the bearing of the
* rotated local +x, clockwise from North. A tilted/flat code gets no
* heading; a rotation-unaware reader then fails LOUD in `parseQrLevel`
* instead of silently placing it as a wall poster. Exported for
* `qr-level.ts`'s both-fields consistency check (milestone review #5).
*/
function deriveVerticalHeading(rotation) {
	const localUp = rotateVectorByQuaternion(rotation, [
		0,
		1,
		0
	]);
	const cosTolerance = Math.cos(VERTICAL_TOLERANCE_DEG * Math.PI / 180);
	if (localUp[1] < cosTolerance) return void 0;
	const localX = rotateVectorByQuaternion(rotation, [
		1,
		0,
		0
	]);
	return normalizeBearingDeg(Math.atan2(localX[2], localX[0]) * 180 / Math.PI);
}
//#endregion
export { deriveVerticalHeading, mintQrGeoPose, renormalizeUnitQuaternion };
